import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan, faRotate, faUserGear } from '@fortawesome/free-solid-svg-icons';
import { userSet } from '../../store/userSlice';
import { del, get } from '../../utils/api';
import useApiGet from '../../hooks/useApiGet';
import AdminToEventList from './AdminToEventList';
import AgentToEventList from './AgentToEventList';
import UserToExhibitorList from './UserToExhibitorList';
import icon_root from '../../assets/root.png';
import icon_admin from '../../assets/admin.png';
import icon_agent from '../../assets/agent.png';
import icon_exhibitor from '../../assets/exhibitor.png';
import './UserView.css';

const icon = {
		root: icon_root,
		admin: icon_admin,
		agent: icon_agent,
		exhibitor: icon_exhibitor,
};

const UserView = () => {
	const auth = useSelector(state => state.auth.user);
	const navigate = useNavigate();
	const dispatch = useDispatch();
    const { id, action } = useParams();
	const method = '/users/' + id;
	const { t } = useTranslation();
	const [ data, error, isLoading ] = useApiGet({ method });
	const [ user, setUser ] = useState(undefined);
	const [ deleteError,   setDeleteError ]   = useState('');
	const [ deleteSuccess, setDeleteSuccess ] = useState('');
	const [ deleteProcess, setDeleteProcess ] = useState(false);
	const [ convertError,   setConvertError ]   = useState('');
	const [ convertSuccess, setConvertSuccess ] = useState('');
	const [ convertProcess, setConvertProcess ] = useState(false);

    // Запишем юзера в стор
	useEffect(() => {
		if(data) {
			dispatch(userSet(data.user));
			setUser(data.user);
		}
	}, [ data, dispatch ]);

	// Конвертация агента в компанию и обратно
	const onConvert = async () => {
		if (user?.role !== 'agent' && user?.role !== 'exhibitor') return;

		setConvertError('');
		setConvertSuccess('');
		setConvertProcess(true);

		let method = `/convert/agentToExhibitor/${user._id}`;
		let confirm = t('userview.convert2exhibitor_confirm');
		if (user.role === 'exhibitor') {
			method = `/convert/exhibitorToAgent/${user._id}`;
			confirm = t('userview.convert2agent_confirm');	
		}

		if(!window.confirm(confirm)) {
			setConvertProcess(false);
			return;
		}

		const result = await get(method);

        if(result.error) {
			setConvertError(t('userview.convert_error') + ':');
        } else {
			dispatch(userSet(result.data.user));
			setUser(result.data.user);
			setConvertSuccess('userview.convert_success');
        }

		setConvertProcess(false);
	};

	// Удаляем юзера
	const onDelete = async () => {
		if (!user) return;
		
		setDeleteError('');
		setDeleteSuccess('');
		setDeleteProcess(true);

		if(!window.confirm(t('userview.delete_confirm'))) {
			setDeleteProcess(false);
			return;
		}

		const result = await del(`/users/${user._id}`);

        if(result.error) {
            setDeleteError(t('userview.delete_error') + ':' + result.error);
        } else {
            setDeleteSuccess('userview.delete_success');
        }
		setDeleteProcess(false);
	};

	return (
	<div>
		<h1>
			{user ? t('user.' + user.role) : t('userview.title')}
		</h1>

		{error && <p className='error'>{t(error)}</p>}
		{deleteError && <p className='error'>{deleteError}</p>}
		{deleteSuccess && <p className='success'>{t(deleteSuccess)}</p>}
		{convertError && <p className='error'>{convertError}</p>}
		{convertSuccess && <p className='success'>{t(convertSuccess)}</p>}
		{action === 'created' && <p className='success'>{t('userview.created')}</p>}
		{action === 'updated' && <p className='success'>{t('userview.updated')}</p>}
		{isLoading && <p>{t('loader.title')}</p>}
		{user &&
		<React.Fragment>
			<div className='User'>
				<img className='icon' alt='icon' src={icon[user.role]}/>
				<div className='text'>
					<div className='name'>{user.name}</div>
					<div className='email'>{user.email}</div>
				</div>
				{/* Для рута и админа выводим кнопки редактирования/удаления/конвертации юзера */}
				{(auth.role === 'root' || auth.role === 'admin') &&
				<React.Fragment>
					{/* Если идет процесс удаления/конвертации, кнопки скрываем и выводим индикатор */}
					{(deleteProcess || convertProcess) ? 
					<React.Fragment>
						<div
							className='menu'
							style={{color:'var(--remove-color)'}}
						>
							<Icon icon={faRotate} size='xl' className='fa-spin'/>
						</div>
					</React.Fragment> :
					<React.Fragment>
						{/* Кнопку конвертации показываем руту если он смотрит агента или компанию */}
						{(auth.role === 'root' && (user.role === 'exhibitor' || user.role === 'agent')) &&
						<div
							className='menu'
							style={{color:'var(--edit-color)'}}
							onClick={() => onConvert()}
						>
							<Icon icon={faUserGear} size='xl'/>
						</div>}
						{/* Кнопку редактирования показываем руту и админу, если он смотрит экспонента */}
						{(auth.role === 'root' || (auth.role === 'admin' && (user.role === 'exhibitor' || user.role === 'agent'))) &&
						<div
							className='menu'
							style={{color:'var(--edit-color)'}}
							onClick={() => navigate('/cabinet/useredit')}
						>
							<Icon icon={faPenToSquare} size='xl'/>
						</div>}
						{/* Кнопку удаления показываем только руту, если смотрим админа или другого рута */}
						{(auth.role === 'root' && (user.role === 'admin' || user.role === 'root') && user._id !== auth._id) &&
						<div
							className='menu'
							onClick={() => onDelete()}
							style={{color:'var(--remove-color)'}}
						>
							<Icon icon={faTrashCan} size='xl'/>
						</div>}
					</React.Fragment>}
				</React.Fragment>}
			</div>

			{user.role === 'admin' && <AdminToEventList admin={user}/>}
			{user.role === 'agent' && <AgentToEventList agent={user}/>}
			{(user.role === 'exhibitor' || user.role === 'agent') && <UserToExhibitorList user={user}/>}
		</React.Fragment>
		}
	</div>
	);
};

export default UserView;
